import React, { useEffect, useMemo, useState } from 'react';

import { type EmojiId } from '@app/client';
import config from '@app/config';
import { usePrevious } from '@app/helpers';

import './YatImageDisplay.scss';

const { imageUrl } = config;
type YatImageDisplayProps = {
    emojiID: string | EmojiId;
    imageHeight: number;
    isLarge?: boolean;
};
export const YatImageDisplay = ({ emojiID, imageHeight, isLarge }: YatImageDisplayProps) => {
    // imageHeight can't be optional any longer, please ensure you always specify a starting height when using
    // then scale the height in the component it's being used
    const [hasLoaded, setHasLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const prevYat = usePrevious(emojiID);

    const imageSrc = useMemo(() => {
        // 150px because our largest image needs to be 75px @2x
        const yatImageUrl = `${imageUrl}/${encodeURI(emojiID)}?padding=0&transparent=1&height=${isLarge ? 150 : 48}`;
        if (hasError) {
            return `${yatImageUrl}?force=1`;
        }
        return yatImageUrl;
    }, [emojiID, hasError, isLarge]);

    useEffect(() => {
        if (emojiID && imageSrc) {
            const img = document.createElement('img');
            img.src = imageSrc;
            img.onload = setImageLoaded;
            img.onerror = setImageError;
        } else {
            setHasLoaded(false);
        }
    }, [emojiID, imageSrc]);

    const setImageLoaded = () => {
        setHasLoaded(true);
    };

    const setImageError = () => {
        setHasLoaded(true);
        setHasError(true);
    };

    const hasLoadedClass = useMemo(() => {
        let className = '';
        if (hasLoaded) {
            className = 'yat-image-display--not-loading';
        }
        return className;
    }, [hasLoaded]);

    useEffect(() => {
        if (emojiID !== prevYat || !imageSrc) {
            setHasLoaded(false);
        }
    }, [prevYat, emojiID, imageSrc]);
    return (
        <div key={emojiID} className={`yat-image-display ${hasLoadedClass}`} style={{ height: imageHeight + 'px' }}>
            {hasError ? <p className="yat-image-display__fallback">{emojiID}</p> : <img alt={emojiID} src={imageSrc} />}
        </div>
    );
};
